import { ChevronRightIcon, type LucideIcon } from 'lucide-react';
import { Badge } from '../components/badge';
import { CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, Command } from '../components/command';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/dialog';
import { Kbd } from '../components/misc';
import { searchEntries, type NavRegistry } from '../navigation';
import { useUiStrings } from '../strings';

export interface PaletteAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  keywords?: readonly string[];
  /** Key caps shown at the right, e.g. "Ctrl+R". */
  shortcut?: string;
  onSelect: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pages come from the same registry as the NavRail (DESIGN.md §9.1). */
  navigation: NavRegistry;
  actions?: readonly PaletteAction[];
  onNavigate: (path: string) => void;
}

/** Global command palette (Ctrl/⌘+K): pages and actions of the current app. */
export function CommandPalette({ open, onOpenChange, navigation, actions = [], onNavigate }: CommandPaletteProps) {
  const strings = useUiStrings();
  const run = (fn: () => void) => {
    onOpenChange(false);
    fn();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-1/4 translate-y-0 overflow-hidden p-0" showCloseButton={false}>
        <DialogTitle className="sr-only">{strings.search}</DialogTitle>
        <DialogDescription className="sr-only">{strings.commandPlaceholder}</DialogDescription>
        <Command>
          <CommandInput placeholder={strings.commandPlaceholder} />
          <CommandList>
            <CommandEmpty>{strings.noResults}</CommandEmpty>
            {actions.length > 0 && (
              <CommandGroup>
                {actions.map(({ id, label, icon: Icon, keywords, shortcut, onSelect }) => (
                  <CommandItem key={id} value={`action-${id} ${label}`} keywords={keywords ? [...keywords] : undefined} onSelect={() => run(onSelect)}>
                    {Icon && <Icon />}
                    {label}
                    {shortcut && <Kbd className="ml-auto">{shortcut}</Kbd>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {navigation.map((group) => (
              <CommandGroup key={group.id} heading={group.label}>
                {searchEntries([group]).map(({ item, tab, section, setting, path }) => {
                  const Icon = item.icon;
                  const id = `${setting ? 'setting' : section ? 'section' : tab ? 'tab' : 'page'}-${item.id}${tab ? `-${tab.id}` : ''}${section ? `-${section.id}` : ''}${setting ? `-${setting.id}` : ''}`;
                  const trail = [...(tab ? [tab.label] : []), ...(section ? [section.label] : []), ...(setting ? [setting.label] : [])];
                  return (
                    <CommandItem
                      key={id}
                      value={`${id} ${item.label} ${trail.join(' ')}`}
                      keywords={[group.label, ...(item.keywords ?? []), ...(tab ? [item.label, ...(tab.keywords ?? [])] : []), ...(section?.keywords ?? []), ...(setting?.keywords ?? [])]}
                      onSelect={() => run(() => onNavigate(path))}
                    >
                      <Icon className={trail.length > 0 ? 'opacity-0' : undefined} />
                      {trail.length > 0 ? (
                        <span className="flex min-w-0 items-center gap-1">
                          <span className="truncate text-muted-foreground">{item.label}</span>
                          {trail.map((label, index) => (
                            <span key={`${label}-${index}`} className="flex min-w-0 items-center gap-1">
                              <ChevronRightIcon aria-hidden className="size-3 shrink-0 text-muted-foreground" />
                              <span className={index < trail.length - 1 ? 'truncate text-muted-foreground' : 'truncate'}>{label}</span>
                            </span>
                          ))}
                        </span>
                      ) : (
                        item.label
                      )}
                      {tab?.status === 'planned' && (
                        <Badge variant="muted" className="ml-auto">
                          {strings.planned}
                        </Badge>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
